/**
 * VÉRAULT Member Suite — Member Command Palette (Wave 26.20 Phase 1b).
 *
 * The client-side counterpart to the advisor `CommandPalette`. Bound to
 * the SAME ⌘K / Ctrl+K shortcut and the same global custom event
 * (`verault:open-command-palette`) so the topbar search button keeps
 * working seamlessly.
 *
 * Phase 1b scope: navigation-only. Six quick links + a quiet editorial
 * promise of what's coming ("Soon: every reservation, restaurant,
 * document, and itinerary item, searchable by name."). No fetch, no
 * results layer — that arrives in Phase 2.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Home, Compass, MessageSquare, CalendarRange, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const QUICK_LINKS = [
  { kind: "nav", label: "Home", sub: "Your overview", to: "/portal/client", Icon: Home },
  { kind: "nav", label: "Experiences", sub: "Your travel shelf", to: "/portal/client/experiences", Icon: Compass },
  { kind: "nav", label: "Messages", sub: "Talk to your advisor", to: "/portal/client/messages", Icon: MessageSquare },
  { kind: "nav", label: "Connect", sub: "Book a moment", to: "/portal/client/connect", Icon: CalendarRange },
  { kind: "nav", label: "Settings", sub: "Account preferences", to: "/portal/client/settings", Icon: SettingsIcon },
];

export default function MemberCommandPalette() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  // Only mounted for clients — advisors get the richer CommandPalette.
  const enabled = user && user.role === "client";

  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    const openHandler = () => setOpen(true);
    window.addEventListener("keydown", handler);
    window.addEventListener("verault:open-command-palette", openHandler);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("verault:open-command-palette", openHandler);
    };
  }, [enabled, open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const filtered = QUICK_LINKS.filter((l) =>
    !q.trim() || l.label.toLowerCase().includes(q.trim().toLowerCase()),
  );

  const choose = (item) => {
    if (!item) return;
    setOpen(false);
    navigate(item.to);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(filtered[activeIdx]);
    }
  };

  if (!enabled || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[9990] bg-black/30 backdrop-blur-[6px] flex items-start justify-center pt-[14vh] px-4 animate-in fade-in duration-200"
      onClick={() => setOpen(false)}
      data-testid="member-command-palette-backdrop"
    >
      <div
        className="w-full max-w-xl rounded-2xl bg-[hsl(var(--background))] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] overflow-hidden border border-black/5"
        onClick={(e) => e.stopPropagation()}
        data-testid="member-command-palette"
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(0,0,0,0.05)]">
          <Search className="w-4 h-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Find a corner of your travel life…"
            data-testid="member-command-palette-input"
            className="flex-1 bg-transparent font-body text-[15px] text-[hsl(var(--foreground))] placeholder-[hsl(var(--foreground-muted))] focus:outline-none"
          />
          <kbd className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] bg-[hsl(var(--background-paper))] px-1.5 py-0.5 rounded">esc</kbd>
        </div>

        {/* Quick links group */}
        <div className="max-h-[60vh] overflow-y-auto py-3">
          <p className="px-5 pt-1 pb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            Quick links
          </p>
          {filtered.map((item, i) => {
            const active = i === activeIdx;
            const Icon = item.Icon;
            return (
              <button
                key={item.to}
                type="button"
                onClick={() => choose(item)}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`member-command-link-${item.label.toLowerCase()}`}
                className={`w-full text-left px-5 py-2.5 flex items-center gap-3 transition-colors duration-150 ${
                  active
                    ? "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground))]"
                    : "hover:bg-[hsl(var(--background-paper))]/60 text-[hsl(var(--foreground))]"
                }`}
              >
                <Icon className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-body">{item.label}</p>
                  <p className="text-[11px] font-body text-[hsl(var(--foreground-muted))]">{item.sub}</p>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="px-5 py-6 font-accent italic text-sm text-[hsl(var(--foreground-muted))] text-center">
              Nothing matched "{q}" — try a section name.
            </p>
          )}

          {/* Editorial promise — Phase 2 will plug real search here */}
          <div className="mt-2 mx-3 mb-2 px-3 py-3 rounded-md bg-[hsl(var(--background-paper))]/40">
            <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Soon
            </p>
            <p className="mt-1.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))] leading-relaxed">
              Every reservation, restaurant, document, and itinerary item —
              searchable by name. Quietly composing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
